import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  MetricsCard,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  type SelectOption,
  SelectTrigger,
  SelectValue,
  SimpleBarChart,
} from "@getdevpulse/shared-ui"
import { observer } from "mobx-react-lite"
import { useCallback, useEffect, useMemo, useState } from "react"
import {
  useDataStore,
  useDoraMetricsStore,
  useI18nStore,
  useMetadataStore,
  useViewStore,
} from "../stores/StoreContext"

export const DoraMetricsPage = observer(() => {
  const doraStore = useDoraMetricsStore()
  const i18nStore = useI18nStore()
  const viewStore = useViewStore()
  const metadataStore = useMetadataStore()
  const dataStore = useDataStore()

  // Get all unique teams from MetadataStore (same as TeamDashboard)
  const teams = metadataStore.uniqueTeams

  // Select first team by default
  const [selectedTeam, setSelectedTeam] = useState<string>(teams[0] || "")

  // Initialize store on mount
  useEffect(() => {
    if (!doraStore.hasData && !doraStore.isLoading) {
      doraStore.initialize().catch((error) => {
        console.error("Failed to load DORA metrics", error)
      })
    }
  }, [doraStore])

  // Create select options for teams (same as TeamDashboard)
  const teamOptions: SelectOption[] = useMemo(
    () =>
      teams.map((team) => ({
        id: team,
        label: team,
      })),
    [teams],
  )

  // Get author handles for selected team (same logic as TeamDashboard)
  const teamAuthorHandles = useMemo(() => {
    if (!selectedTeam) return new Set<string>()

    // teams.json maps author handle -> team name
    // We need to find all handles that belong to the selected team
    const teamMembers = metadataStore.allTeams
      .filter((t) => t.team === selectedTeam)
      .map((t) => t.authorId) // authorId here is actually the handle from teams.json key

    return new Set(teamMembers)
  }, [selectedTeam, metadataStore])

  // Get filtered metrics based on view and team selection
  const activeMetrics = useMemo(() => {
    // In group view with a selected team, filter by team's author handles
    if (viewStore.isGroupView && selectedTeam && teamAuthorHandles.size > 0) {
      // Map handles to author IDs for DORA metrics filtering
      const authorIds: number[] = []
      for (const handle of teamAuthorHandles) {
        const authorId = dataStore.handleToIdMap.get(handle)
        if (authorId !== undefined) {
          authorIds.push(Number(authorId))
        }
      }
      return doraStore.getFilteredDoraMetricsByAuthorList(authorIds)
    }

    // Otherwise, use organization-level metrics
    return doraStore.filteredDoraMetrics
  }, [
    viewStore.isGroupView,
    selectedTeam,
    teamAuthorHandles,
    dataStore.handleToIdMap,
    doraStore.filteredDoraMetrics,
  ])

  // Period type options
  const periodTypeOptions = useMemo(
    () => [
      { id: "week", label: i18nStore.t("doraPeriodWeekly") },
      { id: "month", label: i18nStore.t("doraPeriodMonthly") },
    ],
    [i18nStore.locale, i18nStore.t],
  )

  // View level options (only Organization and Group for DORA)
  const viewLevelOptions = useMemo(
    () => [
      { id: "organization", label: i18nStore.t("viewOrganization") },
      { id: "group", label: i18nStore.t("viewGroup") },
    ],
    [i18nStore.locale],
  )

  // Performance level badge variant
  const getLevelBadge = (level: "elite" | "high" | "medium" | "low") => {
    const variants = {
      elite: "default",
      high: "secondary",
      medium: "outline",
      low: "destructive",
    } as const
    return variants[level]
  }

  // Performance level colors
  const getLevelColor = (level: "elite" | "high" | "medium" | "low") => {
    const colors = {
      elite: "bg-green-500 hover:bg-green-600",
      high: "bg-blue-500 hover:bg-blue-600",
      medium: "bg-yellow-500 hover:bg-yellow-600",
      low: "bg-red-500 hover:bg-red-600",
    }
    return colors[level]
  }

  // Get translated level label
  const getLevelLabel = (level: "elite" | "high" | "medium" | "low") => {
    const keys = {
      elite: "doraLevelElite",
      high: "doraLevelHigh",
      medium: "doraLevelMedium",
      low: "doraLevelLow",
    } as const
    return i18nStore.t(keys[level])
  }

  // Format period label - handles both week and month formats
  // Week format: "2024-W45" -> "Week 45, 2024"
  // Month format: "2024-11" -> "Nov 2024"
  const formatPeriodLabel = useMemo(
    () =>
      (period: string): string => {
        if (period.includes("-W")) {
          // Week format: "2024-W45"
          const [year, weekPart] = period.split("-W")
          return `Week ${weekPart}, ${year}`
        } else {
          // Month format: "2024-11"
          const [year, month] = period.split("-")
          const date = new Date(Number(year), Number(month) - 1)
          return date.toLocaleDateString(i18nStore.locale, { month: "short", year: "numeric" })
        }
      },
    [i18nStore.locale],
  )

  // Constants for time calculations
  const SECONDS_PER_DAY = 86400

  // Helper: Format period key for grouping
  const formatPeriodKey = useCallback(
    (timestamp: number, periodType: "day" | "week" | "month"): string => {
      const date = new Date(timestamp * 1000)
      if (periodType === "month") {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`
      } else if (periodType === "week") {
        const tempDate = new Date(date.getTime())
        tempDate.setHours(0, 0, 0, 0)
        tempDate.setDate(tempDate.getDate() + 4 - (tempDate.getDay() || 7))
        const yearStart = new Date(tempDate.getFullYear(), 0, 1)
        const weekNo = Math.ceil(((tempDate.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
        return `${tempDate.getFullYear()}-W${String(weekNo).padStart(2, "0")}`
      } else {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
      }
    },
    [],
  )

  // Compute deployment frequency from activeMetrics with proper aggregation by period
  const deploymentFrequency = useMemo(() => {
    // Aggregate metrics by period across all repos
    const periodMap = new Map<string, { count: number; totalDays: number }>()

    for (const metric of activeMetrics) {
      const period = formatPeriodKey(metric.period_start, metric.period_type)
      const existing = periodMap.get(period) || { count: 0, totalDays: 0 }
      const durationDays = (metric.period_end - metric.period_start) / SECONDS_PER_DAY

      periodMap.set(period, {
        count: existing.count + metric.deployment_count,
        totalDays: existing.totalDays + durationDays,
      })
    }

    // Convert map to sorted array
    return Array.from(periodMap.entries())
      .map(([period, data]) => ({
        period,
        count: data.count,
        deploymentsPerDay: data.totalDays > 0 ? data.count / data.totalDays : 0,
      }))
      .sort((a, b) => a.period.localeCompare(b.period))
  }, [activeMetrics, formatPeriodKey])

  // Compute lead time metrics from activeMetrics with proper aggregation by period
  const leadTimeForChanges = useMemo(() => {
    const periodMetrics = activeMetrics.filter((m) => m.lead_time_samples > 0)

    // Aggregate metrics by period across all repos using weighted averages
    const periodMap = new Map<
      string,
      {
        totalWeightedAvg: number
        totalWeightedMedian: number
        totalWeightedP95: number
        totalSamples: number
      }
    >()

    for (const metric of periodMetrics) {
      const period = formatPeriodKey(metric.period_start, metric.period_type)
      const existing = periodMap.get(period) || {
        totalWeightedAvg: 0,
        totalWeightedMedian: 0,
        totalWeightedP95: 0,
        totalSamples: 0,
      }

      // Weight by sample count for accurate aggregation
      const samples = metric.lead_time_samples || 0
      periodMap.set(period, {
        totalWeightedAvg: existing.totalWeightedAvg + (metric.lead_time_avg_hours || 0) * samples,
        totalWeightedMedian:
          existing.totalWeightedMedian + (metric.lead_time_median_hours || 0) * samples,
        totalWeightedP95: existing.totalWeightedP95 + (metric.lead_time_p95_hours || 0) * samples,
        totalSamples: existing.totalSamples + samples,
      })
    }

    // Convert map to sorted array with weighted averages
    return Array.from(periodMap.entries())
      .map(([period, data]) => ({
        period,
        averageHours: data.totalSamples > 0 ? data.totalWeightedAvg / data.totalSamples : 0,
        medianHours: data.totalSamples > 0 ? data.totalWeightedMedian / data.totalSamples : 0,
        p95Hours: data.totalSamples > 0 ? data.totalWeightedP95 / data.totalSamples : 0,
      }))
      .sort((a, b) => a.period.localeCompare(b.period))
  }, [activeMetrics, formatPeriodKey])

  // Prepare deployment frequency chart data
  const deploymentFrequencyChartData = useMemo(
    () =>
      deploymentFrequency.map((d) => ({
        label: formatPeriodLabel(d.period),
        value: d.count,
      })),
    [deploymentFrequency, formatPeriodLabel],
  )

  // Prepare lead time chart data
  const leadTimeChartData = useMemo(
    () =>
      leadTimeForChanges.map((d) => ({
        label: formatPeriodLabel(d.period),
        value: Math.round(d.averageHours ?? 0),
      })),
    [leadTimeForChanges, formatPeriodLabel],
  )

  // Prepare lead time detail chart data with both average and median
  const leadTimeDetailChartData = useMemo(
    () =>
      leadTimeForChanges.map((d) => ({
        label: formatPeriodLabel(d.period),
        average: Math.round(d.averageHours ?? 0),
        median: Math.round(d.medianHours ?? 0),
      })),
    [leadTimeForChanges, formatPeriodLabel],
  )

  // Compute average deployment frequency
  const averageDeploymentFrequency = useMemo(() => {
    if (activeMetrics.length === 0) {
      return { perDay: 0, perWeek: 0, perMonth: 0 }
    }

    let totalDeployments = 0
    let totalDays = 0

    for (const metric of activeMetrics) {
      totalDeployments += metric.deployment_count
      const days = Math.ceil((metric.period_end - metric.period_start) / (60 * 60 * 24))
      totalDays += days
    }

    if (totalDays === 0) {
      return { perDay: 0, perWeek: 0, perMonth: 0 }
    }

    const perDay = totalDeployments / totalDays

    return {
      perDay,
      perWeek: perDay * 7,
      perMonth: perDay * 30,
    }
  }, [activeMetrics])

  // Compute average lead time
  const averageLeadTime = useMemo(() => {
    const metricsWithLeadTime = activeMetrics.filter((m) => m.lead_time_samples > 0)

    if (metricsWithLeadTime.length === 0) {
      return { hours: 0, days: 0 }
    }

    let totalWeightedHours = 0
    let totalSamples = 0

    for (const metric of metricsWithLeadTime) {
      if (metric.lead_time_avg_hours !== undefined && metric.lead_time_samples > 0) {
        totalWeightedHours += metric.lead_time_avg_hours * metric.lead_time_samples
        totalSamples += metric.lead_time_samples
      }
    }

    if (totalSamples === 0) {
      return { hours: 0, days: 0 }
    }

    const avgHours = totalWeightedHours / totalSamples

    return {
      hours: avgHours,
      days: avgHours / 24,
    }
  }, [activeMetrics])

  // Compute most recent deployment frequency (for display)
  const mostRecentDeploymentFrequency = useMemo(() => {
    if (deploymentFrequency.length === 0) {
      return { count: 0, period: "" }
    }
    // Get the last (most recent) period
    const mostRecent = deploymentFrequency[deploymentFrequency.length - 1]
    return {
      count: mostRecent.count,
      period: formatPeriodLabel(mostRecent.period),
    }
  }, [deploymentFrequency, formatPeriodLabel])

  // Compute most recent lead time (for display)
  const mostRecentLeadTime = useMemo(() => {
    if (leadTimeForChanges.length === 0) {
      return { hours: 0, days: 0, period: "" }
    }
    // Get the last (most recent) period
    const mostRecent = leadTimeForChanges[leadTimeForChanges.length - 1]
    const hours = mostRecent.averageHours || 0
    return {
      hours,
      days: hours / 24,
      period: formatPeriodLabel(mostRecent.period),
    }
  }, [leadTimeForChanges, formatPeriodLabel])

  // Compute most recent change failure rate (for display)
  const mostRecentChangeFailureRate = useMemo(() => {
    if (doraStore.changeFailureRate.length === 0) {
      return { rate: 0, period: "" }
    }
    // Get the last (most recent) period
    const mostRecent = doraStore.changeFailureRate[doraStore.changeFailureRate.length - 1]
    return {
      rate: mostRecent.failureRate || 0,
      period: formatPeriodLabel(mostRecent.period),
    }
  }, [doraStore.changeFailureRate, formatPeriodLabel])

  // Compute most recent time to restore (for display)
  const mostRecentTimeToRestore = useMemo(() => {
    if (doraStore.timeToRestore.length === 0) {
      return { hours: 0, period: "" }
    }
    // Get the last (most recent) period
    const mostRecent = doraStore.timeToRestore[doraStore.timeToRestore.length - 1]
    return {
      hours: mostRecent.averageHours || 0,
      period: formatPeriodLabel(mostRecent.period),
    }
  }, [doraStore.timeToRestore, formatPeriodLabel])

  // Calculate deployment frequency percentage trend
  const deploymentFrequencyPercentage = useMemo(() => {
    if (deploymentFrequency.length === 0) {
      return { percentage: 0, trend: "neutral" as const }
    }

    const currentValue =
      doraStore.periodType === "week"
        ? averageDeploymentFrequency.perWeek
        : averageDeploymentFrequency.perMonth

    if (viewStore.isGroupView && selectedTeam && teamAuthorHandles.size > 0) {
      // Group view: Compare to average of all teams
      // Get metrics for all teams
      const allTeamMetrics = teams
        .map((team) => {
          const teamMembers = metadataStore.allTeams
            .filter((t) => t.team === team)
            .map((t) => t.authorId)
          const authorIds: number[] = []
          for (const handle of teamMembers) {
            const authorId = dataStore.handleToIdMap.get(handle)
            if (authorId !== undefined) {
              authorIds.push(Number(authorId))
            }
          }
          const teamMetrics = doraStore.getFilteredDoraMetricsByAuthorList(authorIds)

          if (teamMetrics.length === 0) return 0

          let totalDeployments = 0
          let totalDays = 0
          for (const metric of teamMetrics) {
            totalDeployments += metric.deployment_count
            const days = Math.ceil((metric.period_end - metric.period_start) / (60 * 60 * 24))
            totalDays += days
          }

          if (totalDays === 0) return 0
          const perDay = totalDeployments / totalDays
          return doraStore.periodType === "week" ? perDay * 7 : perDay * 30
        })
        .filter((val) => val > 0)

      if (allTeamMetrics.length === 0) {
        return { percentage: 0, trend: "neutral" as const }
      }

      const averageValue = allTeamMetrics.reduce((sum, val) => sum + val, 0) / allTeamMetrics.length

      if (averageValue === 0) {
        return { percentage: 0, trend: "neutral" as const }
      }

      const percentage = ((currentValue - averageValue) / averageValue) * 100
      const trend =
        percentage > 0 ? ("up" as const) : percentage < 0 ? ("down" as const) : ("neutral" as const)

      return { percentage, trend }
    } else {
      // Organization view: Compare to best value in seen periods
      const bestValue = Math.max(...deploymentFrequency.map((d) => d.count), 0)

      if (bestValue === 0) {
        return { percentage: 0, trend: "neutral" as const }
      }

      // Calculate percentage relative to best period
      const recentPeriods = deploymentFrequency.slice(-6)
      const recentAvg =
        recentPeriods.length > 0
          ? recentPeriods.reduce((sum, d) => sum + d.count, 0) / recentPeriods.length
          : 0

      const percentage = ((recentAvg - bestValue) / bestValue) * 100
      const trend =
        percentage > 0 ? ("up" as const) : percentage < 0 ? ("down" as const) : ("neutral" as const)

      return { percentage, trend }
    }
  }, [
    deploymentFrequency,
    averageDeploymentFrequency,
    viewStore.isGroupView,
    selectedTeam,
    teamAuthorHandles,
    teams,
    metadataStore.allTeams,
    dataStore.handleToIdMap,
    doraStore,
  ])

  // Calculate lead time percentage trend
  const leadTimePercentage = useMemo(() => {
    if (leadTimeForChanges.length === 0) {
      return { percentage: 0, trend: "neutral" as const }
    }

    const currentValue = averageLeadTime.hours

    if (viewStore.isGroupView && selectedTeam && teamAuthorHandles.size > 0) {
      // Group view: Compare to average of all teams
      const allTeamMetrics = teams
        .map((team) => {
          const teamMembers = metadataStore.allTeams
            .filter((t) => t.team === team)
            .map((t) => t.authorId)
          const authorIds: number[] = []
          for (const handle of teamMembers) {
            const authorId = dataStore.handleToIdMap.get(handle)
            if (authorId !== undefined) {
              authorIds.push(Number(authorId))
            }
          }
          const teamMetrics = doraStore.getFilteredDoraMetricsByAuthorList(authorIds)
          const metricsWithLeadTime = teamMetrics.filter((m) => m.lead_time_samples > 0)

          if (metricsWithLeadTime.length === 0) return 0

          let totalWeightedHours = 0
          let totalSamples = 0
          for (const metric of metricsWithLeadTime) {
            if (metric.lead_time_avg_hours !== undefined && metric.lead_time_samples > 0) {
              totalWeightedHours += metric.lead_time_avg_hours * metric.lead_time_samples
              totalSamples += metric.lead_time_samples
            }
          }

          return totalSamples > 0 ? totalWeightedHours / totalSamples : 0
        })
        .filter((val) => val > 0)

      if (allTeamMetrics.length === 0) {
        return { percentage: 0, trend: "neutral" as const }
      }

      const averageValue = allTeamMetrics.reduce((sum, val) => sum + val, 0) / allTeamMetrics.length

      if (averageValue === 0) {
        return { percentage: 0, trend: "neutral" as const }
      }

      const percentage = ((currentValue - averageValue) / averageValue) * 100
      // For lead time, lower is better, so invert the trend
      const trend =
        percentage < 0 ? ("up" as const) : percentage > 0 ? ("down" as const) : ("neutral" as const)

      return { percentage, trend }
    } else {
      // Organization view: Compare to best (lowest) value in seen periods
      const bestValue = Math.min(
        ...leadTimeForChanges.map((d) => d.averageHours).filter((h) => h > 0),
        currentValue,
      )

      if (bestValue === 0) {
        return { percentage: 0, trend: "neutral" as const }
      }

      const percentage = ((currentValue - bestValue) / bestValue) * 100
      // For lead time, lower is better, so invert the trend
      const trend =
        percentage < 0 ? ("up" as const) : percentage > 0 ? ("down" as const) : ("neutral" as const)

      return { percentage, trend }
    }
  }, [
    leadTimeForChanges,
    averageLeadTime,
    viewStore.isGroupView,
    selectedTeam,
    teamAuthorHandles,
    teams,
    metadataStore.allTeams,
    dataStore.handleToIdMap,
    doraStore,
  ])

  // Prepare change failure rate chart data (kept for compatibility, but no data available)
  const changeFailureRateChartData = useMemo(
    () =>
      doraStore.changeFailureRate.map((d) => ({
        label: formatPeriodLabel(d.period),
        value: Math.round((d.failureRate ?? 0) * 10) / 10, // Round to 1 decimal
      })),
    [doraStore.changeFailureRate, formatPeriodLabel],
  )

  // Prepare time to restore chart data (kept for compatibility, but no data available)
  const timeToRestoreChartData = useMemo(
    () =>
      doraStore.timeToRestore.map((d) => ({
        label: formatPeriodLabel(d.period),
        value: Math.round(d.averageHours ?? 0),
      })),
    [doraStore.timeToRestore, formatPeriodLabel],
  )

  // Determine subtitle based on view level and selected team
  const subtitle = useMemo(() => {
    if (viewStore.isGroupView && selectedTeam) {
      return i18nStore.t("doraMetricsSubtitleTeam").replace("{team}", selectedTeam)
    }
    return i18nStore.t("doraMetricsSubtitle")
  }, [viewStore.currentView, selectedTeam, i18nStore.locale])

  // Loading state
  if (doraStore.isLoading) {
    return (
      <div className="container mx-auto py-4 sm:py-6 md:py-8">
        <PageHeader
          title={i18nStore.t("doraMetricsTitle")}
          subtitle={i18nStore.t("doraMetricsSubtitle")}
        />
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-white mx-auto mb-4" />
            <p className="text-lg">{i18nStore.t("loading")}</p>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (doraStore.loadError) {
    return (
      <div className="container mx-auto py-4 sm:py-6 md:py-8">
        <PageHeader
          title={i18nStore.t("doraMetricsTitle")}
          subtitle={i18nStore.t("doraMetricsSubtitle")}
        />
        <Alert variant="destructive">
          <AlertTitle>{i18nStore.t("error")}</AlertTitle>
          <AlertDescription>{doraStore.loadError}</AlertDescription>
        </Alert>
      </div>
    )
  }

  // No data state
  if (!doraStore.hasData) {
    return (
      <div className="container mx-auto py-4 sm:py-6 md:py-8">
        <PageHeader
          title={i18nStore.t("doraMetricsTitle")}
          subtitle={i18nStore.t("doraMetricsSubtitle")}
        />
        <Alert>
          <AlertTitle>{i18nStore.t("doraNoDataTitle")}</AlertTitle>
          <AlertDescription>{i18nStore.t("doraNoDataDescription")}</AlertDescription>
        </Alert>
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>{i18nStore.t("doraMissingDataTitle")}</CardTitle>
            <CardDescription>{i18nStore.t("doraMissingDataDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="list-disc list-inside space-y-2">
              {doraStore.dataCompleteness.missingDataPoints.map((point) => (
                <li key={point} className="text-sm text-muted-foreground">
                  {i18nStore.t(point)}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    )
  }

  const performanceLevel = doraStore.performanceLevel
  const dataQuality = doraStore.dataCompleteness.dataQuality
  const isPartialMetrics = dataQuality === "estimated"

  return (
    <div className="container mx-auto py-4 sm:py-6 md:py-8">
      <PageHeader
        title={i18nStore.t("doraMetricsTitle")}
        subtitle={subtitle}
        rightContent={
          <div className="flex gap-3 flex-wrap">
            {/* View Level Selector */}
            <Select
              value={viewStore.currentView}
              onValueChange={(value) => viewStore.setView(value as "organization" | "group")}
            >
              <SelectTrigger className="w-[180px]" aria-label="View Level">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {viewLevelOptions.map((option) => (
                  <SelectItem key={option.id} value={option.id}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Team Selector (only shown in Group view) */}
            {viewStore.isGroupView && teamOptions.length > 0 && (
              <Select value={selectedTeam || ""} onValueChange={(value) => setSelectedTeam(value)}>
                <SelectTrigger className="w-[180px]" aria-label="Select Team">
                  <SelectValue placeholder={i18nStore.t("selectTeam")} />
                </SelectTrigger>
                <SelectContent>
                  {teamOptions.map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Period Type Selector */}
            <Select
              value={doraStore.periodType}
              onValueChange={(value) => doraStore.setPeriodType(value as "week" | "month")}
            >
              <SelectTrigger
                className="w-[150px]"
                aria-label={i18nStore.t("doraPeriodSelectLabel")}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {periodTypeOptions.map((option) => (
                  <SelectItem key={option.id} value={option.id}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />

      {/* Partial Metrics Notice - shown when using Phase 1 data only */}
      {isPartialMetrics && (
        <Alert className="mb-8 border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
          <AlertTitle className="flex items-center gap-2">
            {i18nStore.t("doraPartialMetricsTitle")}
            <Badge
              variant="outline"
              className="border-amber-500 text-amber-700 dark:text-amber-400"
            >
              {i18nStore.t("doraEstimatedBadge")}
            </Badge>
          </AlertTitle>
          <AlertDescription className="text-amber-800 dark:text-amber-300">
            {i18nStore.t("doraPartialMetricsDescription")}
          </AlertDescription>
        </Alert>
      )}

      {/* Overall Performance Level */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            {i18nStore.t("doraOverallPerformance")}
            <Badge
              variant={getLevelBadge(performanceLevel.overall)}
              className={getLevelColor(performanceLevel.overall)}
            >
              {getLevelLabel(performanceLevel.overall)}
            </Badge>
            {isPartialMetrics && (
              <Badge
                variant="outline"
                className="text-xs border-amber-500 text-amber-700 dark:text-amber-400"
              >
                {i18nStore.t("doraEstimatedBadge")}
              </Badge>
            )}
          </CardTitle>
          <CardDescription>{i18nStore.t("doraOverallDescription")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <p className="text-sm font-medium">{i18nStore.t("doraDeploymentFrequency")}</p>
              <Badge
                variant={getLevelBadge(performanceLevel.deploymentFrequency)}
                className={getLevelColor(performanceLevel.deploymentFrequency)}
              >
                {getLevelLabel(performanceLevel.deploymentFrequency)}
              </Badge>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">{i18nStore.t("doraLeadTime")}</p>
              <Badge
                variant={getLevelBadge(performanceLevel.leadTime)}
                className={getLevelColor(performanceLevel.leadTime)}
              >
                {getLevelLabel(performanceLevel.leadTime)}
              </Badge>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">{i18nStore.t("doraChangeFailureRate")}</p>
              <Badge
                variant={getLevelBadge(performanceLevel.changeFailureRate)}
                className={getLevelColor(performanceLevel.changeFailureRate)}
              >
                {getLevelLabel(performanceLevel.changeFailureRate)}
              </Badge>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">{i18nStore.t("doraTimeToRestore")}</p>
              <Badge
                variant={getLevelBadge(performanceLevel.timeToRestore)}
                className={getLevelColor(performanceLevel.timeToRestore)}
              >
                {getLevelLabel(performanceLevel.timeToRestore)}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Completeness Warning */}
      {doraStore.dataCompleteness.missingDataPoints.length > 0 && (
        <Alert className="mb-8">
          <AlertTitle>{i18nStore.t("doraDataLimitationsTitle")}</AlertTitle>
          <AlertDescription>
            <p className="mb-2">{i18nStore.t("doraDataLimitationsDescription")}</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              {doraStore.dataCompleteness.missingDataPoints.map((point) => (
                <li key={point}>{i18nStore.t(point)}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Metrics Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Deployment Frequency */}
        {doraStore.dataCompleteness.hasReleases ? (
          <MetricsCard
            title={i18nStore.t("doraDeploymentFrequency")}
            description={`${mostRecentDeploymentFrequency.count} (${mostRecentDeploymentFrequency.period})`}
            percentage={deploymentFrequencyPercentage.percentage}
            trend={deploymentFrequencyPercentage.trend}
            chartData={deploymentFrequencyChartData.slice(-6)} // Last 6 periods
            chartType="bar"
            distributionLabel={i18nStore.t(
              doraStore.periodType === "week" ? "doraDeploymentsByWeek" : "doraDeploymentsByMonth",
            )}
          />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>{i18nStore.t("doraDeploymentFrequency")}</CardTitle>
              <CardDescription>{i18nStore.t("doraNoDataAvailable")}</CardDescription>
            </CardHeader>
            <CardContent>
              <Alert>
                <AlertDescription>{i18nStore.t("doraMissingReleases")}</AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        )}

        {/* Lead Time for Changes */}
        {doraStore.dataCompleteness.hasPullRequests ? (
          <MetricsCard
            title={i18nStore.t("doraLeadTime")}
            description={`${mostRecentLeadTime.days.toFixed(1)} ${i18nStore.t("doraDays")} (${mostRecentLeadTime.period})`}
            percentage={leadTimePercentage.percentage}
            trend={leadTimePercentage.trend}
            chartData={leadTimeChartData.slice(-6)} // Last 6 months
            chartType="line"
            trendLabel={i18nStore.t("doraLeadTimeTrend")}
          />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>{i18nStore.t("doraLeadTime")}</CardTitle>
              <CardDescription>{i18nStore.t("doraNoDataAvailable")}</CardDescription>
            </CardHeader>
            <CardContent>
              <Alert>
                <AlertDescription>{i18nStore.t("doraMissingPullRequests")}</AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        )}

        {/* Change Failure Rate */}
        {doraStore.dataCompleteness.hasWorkflowRuns ? (
          <MetricsCard
            title={i18nStore.t("doraChangeFailureRate")}
            description={`${mostRecentChangeFailureRate.rate.toFixed(1)}% (${mostRecentChangeFailureRate.period})`}
            percentage={0}
            trend="neutral"
            chartData={changeFailureRateChartData.slice(-6)} // Last 6 months
            chartType="line"
            trendLabel={i18nStore.t("doraFailureRateTrend")}
          />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>{i18nStore.t("doraChangeFailureRate")}</CardTitle>
              <CardDescription>{i18nStore.t("doraNoDataAvailable")}</CardDescription>
            </CardHeader>
            <CardContent>
              <Alert>
                <AlertDescription>{i18nStore.t("doraMissingWorkflowRuns")}</AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        )}

        {/* Time to Restore Service */}
        {doraStore.dataCompleteness.hasIncidents ? (
          <MetricsCard
            title={i18nStore.t("doraTimeToRestore")}
            description={`${mostRecentTimeToRestore.hours.toFixed(1)} ${i18nStore.t("doraHours")} (${mostRecentTimeToRestore.period})`}
            percentage={0}
            trend="neutral"
            chartData={timeToRestoreChartData.slice(-6)} // Last 6 months
            chartType="line"
            trendLabel={i18nStore.t("doraRestoreTimeTrend")}
          />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>{i18nStore.t("doraTimeToRestore")}</CardTitle>
              <CardDescription>{i18nStore.t("doraNoDataAvailable")}</CardDescription>
            </CardHeader>
            <CardContent>
              <Alert>
                <AlertDescription>{i18nStore.t("doraMissingIncidents")}</AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Detailed Charts */}
      <div className="grid grid-cols-1 gap-6">
        {/* Deployment Frequency Detail */}
        {doraStore.dataCompleteness.hasReleases && deploymentFrequencyChartData.length > 0 && (
          <SimpleBarChart
            title={i18nStore.t("doraDeploymentFrequencyDetail")}
            description={i18nStore.t("doraDeploymentFrequencyDetailDescription")}
            data={deploymentFrequencyChartData}
          />
        )}

        {/* Lead Time Detail */}
        {doraStore.dataCompleteness.hasPullRequests && leadTimeDetailChartData.length > 0 && (
          <SimpleBarChart
            title={i18nStore.t("doraLeadTimeDetail")}
            description={i18nStore.t("doraLeadTimeDetailDescription")}
            data={leadTimeDetailChartData}
          />
        )}
      </div>

      {/* Methodology Note */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>{i18nStore.t("doraMethodologyTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-muted-foreground">
          <div>
            <p className="font-medium text-foreground mb-1">
              {i18nStore.t("doraDeploymentFrequency")}:
            </p>
            <p>{i18nStore.t("doraDeploymentFrequencyMethodology")}</p>
          </div>
          <div>
            <p className="font-medium text-foreground mb-1">{i18nStore.t("doraLeadTime")}:</p>
            <p>{i18nStore.t("doraLeadTimeMethodology")}</p>
          </div>
          <div>
            <p className="font-medium text-foreground mb-1">
              {i18nStore.t("doraChangeFailureRate")}:
            </p>
            <p>{i18nStore.t("doraChangeFailureRateMethodology")}</p>
          </div>
          <div>
            <p className="font-medium text-foreground mb-1">{i18nStore.t("doraTimeToRestore")}:</p>
            <p>{i18nStore.t("doraTimeToRestoreMethodology")}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
})
